import { useMemo } from "react";

import moment from "moment-timezone";

import type { Quote } from "~/v2/api/rest/data";

export interface QuoteDisplayResult {
  bid: string;
  ask: string;
  midPrice: { value: number; display: string };
  contractPrice: string;
  quoteLastUpdated: string | undefined;
  marketPrice: number;
}

export const getQuoteDisplay = (
  quote: Quote | undefined,
  side: "buy" | "sell",
): QuoteDisplayResult => {
  const precision = (quote?.last ?? 0) < 1.0 ? 4 : 2;
  const localeOpts = {
    minimumFractionDigits: 2,
    maximumFractionDigits: precision,
  };

  const bid =
    quote?.bid != null
      ? Number(quote.bid.toFixed(precision)).toLocaleString("en-US", localeOpts)
      : "-";

  const ask =
    quote?.ask != null
      ? Number(quote.ask.toFixed(precision)).toLocaleString("en-US", localeOpts)
      : "-";

  let midPrice: QuoteDisplayResult["midPrice"];

  if (quote?.bid == null || quote?.ask == null) {
    midPrice = { value: 0, display: "-" };
  } else {
    const mid = (quote.bid + quote.ask) / 2;
    midPrice = {
      value: mid,
      display: Number(mid.toFixed(precision)).toLocaleString(
        "en-US",
        localeOpts,
      ),
    };
  }

  const contractPrice = quote?.last
    ? `$${Number(quote.last.toFixed(precision)).toLocaleString("en-US", localeOpts)}`
    : "-";

  const quoteLastUpdated = quote?.bid_timestamp
    ? moment(quote.bid_timestamp)
        .tz("America/New_York")
        .format("MM/DD HH:mm:ss")
    : undefined;

  const marketPrice = (side === "buy" ? quote?.ask : quote?.bid) ?? 0;

  return { bid, ask, midPrice, contractPrice, quoteLastUpdated, marketPrice };
};

interface UseQuoteDisplayProps {
  quote: Quote | undefined;
  side: "buy" | "sell";
}

export const useQuoteDisplay = ({ quote, side }: UseQuoteDisplayProps) =>
  useMemo(() => getQuoteDisplay(quote, side), [quote, side]);
